import React from 'react';
import styled from '@emotion/styled';

import Typography from '@mui/material/Typography';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import Tooltip from '@mui/material/Tooltip';
import SharedDropdownMenu from "@imminently/decisively-ui/DropdownMenu";

import { useTheme } from '@mui/material';
import { Label } from '@/components';


const ItemIcon = styled(ListItemIcon)`
  margin-right: 0.5rem;
  min-width: 0;
`;

const ItemText = styled(ListItemText)`
  margin: 0;
`;


/** @deprecated */
export const MenuItem = styled(ListItem)`
  &:hover {
    background-color: var(--secondary-hover);
  }
`;

/** @deprecated */
export const Action = styled(ListItemSecondaryAction)`
  right: 1rem;
`;

const smallText = {
  fontSize: '0.75rem',
  fontWeight: 700,
};

const DefaultItemText = ({ name, small }) => (
  <ItemText primary={name} primaryTypographyProps={{ noWrap: true, style: small ? smallText : undefined }} />
);

/** @deprecated */
export const Item = ({ name, text, icon, action, small, ...props }) => (
  <SharedDropdownMenu.Item {...props}>
    {icon && <ItemIcon>{icon}</ItemIcon>}
    {
      name && name.length > 16 ? (
        <Tooltip title={name}>
          {text || <DefaultItemText name={name} small={small} />}
        </Tooltip>
      ) : (
        text || <DefaultItemText name={name} small={small} />
      )
    }
    {action && <Action>{action}</Action>}
  </SharedDropdownMenu.Item>
);

// ===================================================================================

const AsyncSelectItemTooltip = styled(Tooltip)`
  & .MuiTooltip-tooltip {
    margin-left: 30px;
  }
`;

const AsyncSelectItemTooltipForRelease = styled(Tooltip)`
  & .MuiTooltip-tooltip {
    margin-left: 10px;
  }
`;


/**
 * @type { React.FC< { name: string; description?: string; } > }
 * @deprecated
 */
export const AsyncSelectItemTooltipTitle = (() => {
  const Name = styled(Typography)`
    font-weight: bold;
  `;

  const comp = React.memo(({ name, description }) => {
    const truncatedDesc = React.useMemo(() => (
      (Boolean(description) === false || description.length <= 240)
        ? description
        /** 240 is a special length for current width/height of 10rem/14rem */
        : `${description.slice(0, 240)}...`
    ), [description]);


    return (
      <div className="max-h-[22.25rem] max-w-[12.666rem]">
        <Name variant='caption'>{name}</Name>
        {
          description && (
            <>
              <br />
              <Typography variant='caption'>
                {truncatedDesc}
              </Typography>
            </>
          )
        }
      </div>
    );
  });
  comp.displayName = 'AsyncSelectItemTooltipTitle';

  return comp;
})();

const AsyncSelectItemMenuItem = styled(ListItem)`
  width: 100%;

  & .MuiListItemSecondaryAction-root {
    right: 1rem;
  }

  padding: 0.5rem 1rem;

  &:hover {
    background-color: initial;
  }

  & .MuiListItemText-root:nth-of-type(2) {
    padding-right: 1.4rem;
  }
`;

/**
  @type {React.FC<{
    name: string;
    icon?: JSX.Element;
    action?: JSX.Element;
    small?: boolean;
    disabled?: boolean;
    description?: string;
    isRelease?: boolean;
    releaseEnv?: string;
    isCurrent: boolean
  }>}
  * @deprecated
  */
export const AsyncSelectItem = React.memo(({
  name, icon, action, small, disabled, description, isRelease = false, releaseEnv,
  isCurrent,
}) => {
  const theme = useTheme();

  return (
    isRelease
      ? (
        <AsyncSelectItemTooltipForRelease
          title={<AsyncSelectItemTooltipTitle name={name} description={description} />}
          placement='right'
        >
          <ListItem style={{ justifyContent: 'space-between' }} selected={isCurrent}>
            <Typography variant='body1'>{name}</Typography>
            {releaseEnv && <Label color={theme.mixins.labels['Invite only']}>{releaseEnv}</Label>}
          </ListItem>
        </AsyncSelectItemTooltipForRelease>
      )
      : (
        <AsyncSelectItemMenuItem button ContainerComponent={ContainerItem} disabled={disabled} selected={isCurrent}>
          {icon && <ItemIcon>{icon}</ItemIcon>}
          <AsyncSelectItemTooltip
            title={<AsyncSelectItemTooltipTitle name={name} description={description} />}
            placement='right'
          >
            <ItemText primary={name} primaryTypographyProps={{ noWrap: true, style: small ? smallText : undefined }} />
          </AsyncSelectItemTooltip>
          {action && <Action>{action}</Action>}
        </AsyncSelectItemMenuItem>
      )
  );
});
AsyncSelectItem.displayName = 'AsyncSelectItem';
